// ABOUTME: Error boundary that catches React errors and shows a pure HTML fallback
// ABOUTME: Uses vanilla JS for reset to avoid any React/dependency issues

import type { ReactNode } from "react"
import React from "react"
import * as Sentry from "@sentry/react"
import { addLogError } from "@/model/logger"
import { isChunkLoadError, reloadForChunkError } from "@/model/watchError.fn"
import { clearAllIndexedDB } from "./clearIndexedDB"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

/**
 * Performs a full application reset using vanilla JS (no React dependencies).
 * Clears all storage, caches, service workers, and reloads.
 */
async function fullResetVanilla(): Promise<void> {
  try {
    localStorage.clear()
  } catch {
    /* ignore */
  }

  try {
    sessionStorage.clear()
  } catch {
    /* ignore */
  }

  try {
    if ("caches" in window) {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map((name) => caches.delete(name)))
    }
  } catch {
    /* ignore */
  }

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((reg) => reg.unregister()))
    }
  } catch {
    /* ignore */
  }

  try {
    await clearAllIndexedDB()
  } catch {
    /* ignore */
  }

  window.location.href = `${window.location.origin}?reset=${Date.now()}`
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, errorInfo: null }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Stale lazy-chunk after a deploy — auto-reload so the user gets the
    // new index.html instead of being parked on the "Reset and Reload" UI.
    if (isChunkLoadError(error)) {
      reloadForChunkError()
      return
    }
    this.setState({ errorInfo })
    // This capture must come first: it carries the React component stack. addLogError then tries to capture
    // the same Error again and the SDK skips it (@sentry/core checkOrSetAlreadyCaught marks the object on the
    // first capture) — in the other order the enriched capture would be the one dropped.
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack ?? undefined } } })
    addLogError("ErrorBoundary/componentDidCatch", errorInfo, error)
  }

  override componentDidUpdate(_prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    // When error occurs, inject vanilla JS error UI directly into DOM
    if (this.state.hasError && !prevState.hasError) {
      this.showVanillaErrorUI()
    }
  }

  showVanillaErrorUI() {
    // Detect language from localStorage or navigator
    const storedLang = localStorage.getItem(STORAGE_KEYS.LANGUAGE) || navigator.language || "en"
    const isRu = storedLang.startsWith("ru")

    const errorMsg = isRu ? "Произошла непредвиденная ошибка." : "An unexpected error has occurred."
    const btnText = isRu ? "Сбросить и обновить" : "Reset and Reload"
    const resetText = isRu ? "Сброс..." : "Resetting..."

    // Find root element and inject vanilla HTML
    const root = document.getElementById("root")
    if (root) {
      root.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:20px;text-align:center;font-family:system-ui,-apple-system,sans-serif;background:#f9fafb">
          <div style="max-width:320px">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5" style="display:block;margin:0 auto 16px">
              <path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p style="margin:0 0 24px;font-size:16px;color:#374151">${errorMsg}</p>
            <button id="error-reset-btn" style="width:100%;padding:12px 24px;font-size:14px;font-weight:500;color:#fff;background:#2563eb;border:none;border-radius:8px;cursor:pointer">${btnText}</button>
          </div>
        </div>
      `

      const btn = document.getElementById("error-reset-btn")
      if (btn) {
        btn.onclick = async () => {
          btn.textContent = resetText
          btn.setAttribute("disabled", "true")
          btn.style.opacity = "0.5"
          btn.style.cursor = "not-allowed"
          await fullResetVanilla()
        }
      }
    }
  }

  override render() {
    const { hasError } = this.state
    const { children } = this.props

    // When error occurs, return null - the vanilla UI is injected directly into DOM
    if (hasError) {
      return null
    }

    return children
  }
}
